import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import type { ChartConfiguration } from 'chart.js';
import { cloneDeep } from 'lodash';
import { NgChartsModule } from 'ng2-charts';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from 'src/environments/environment';
import { ApiServiceService } from '../api-service.service';
import { CommonDropdownSearchComponent } from '../common-component-dropdown-search/common-component-dropdown-search.component';
import { CommonInfoTooltipComponent } from '../common-component-info-tooltip/common-component-info-tooltip.component';
import { CommonPaginationComponent } from '../common-component-pagination/common-component-pagination.component';
import { CommonSearchComponent } from '../common-component-search/common-component-search.component';
import { MultiSelectDropdownComponent } from '../common-multi-select-component-dropdown-search/common-multi-select-component-dropdown-search.component';
import { buildChartTheme } from '../shared/utils/chart-theme.util';
import { chartColorsList } from '../shared/variables/chartColorsList';
import { IndianNumberPipe } from '../pipes/indian-number.pipe';
import {
  deviceDashboardGraphInfo,
  deviceDashboardKpiInfo,
} from './device-dashboard-page.info';

@Component({
  selector: 'app-device-dashboard-page',
  templateUrl: './device-dashboard-page.component.html',
  styleUrls: ['./device-dashboard-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NgChartsModule,
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    MatTooltip,
    NgxPaginationModule,
    CommonSearchComponent,
    CommonDropdownSearchComponent,
    CommonPaginationComponent,
    MultiSelectDropdownComponent,
    IndianNumberPipe,
    CommonInfoTooltipComponent,
  ],
})
export class DeviceDashboardPageComponent implements OnInit, OnDestroy {
  @ViewChild('tableContainer') MyProp!: ElementRef;
  projectName = environment.projectName;

  // KPI Count
  productionDeviceCount = 0;
  dispatchedCount = 0;
  aisCount = 0;
  installedCount = 0;

  // Plain-language explanations shown by the info-tooltip icons on each KPI
  // card and graph, kept in their own file (imported above) for isolation
  // from this component's logic.
  kpiInfo = deviceDashboardKpiInfo;
  graphInfo = deviceDashboardGraphInfo;

  // Graph Data
  activeLabel = [
    'Today',
    'Five Days',
    'Ten Days',
    'Fifteen Days',
    'More Than Fifteen',
    'Not Active',
  ];
  firmwareChartLabel: string[] = [];
  firmwareWiseDeviceBarChartData: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor: string[];
      borderColor: string[];
      borderWidth: number;
    }[];
  } = {
    labels: [],
    datasets: [
      {
        label: 'Count',
        data: [],
        backgroundColor: chartColorsList,
        borderColor: chartColorsList,
        borderWidth: 1,
      },
    ],
  };
  stateChartLabel: string[] = [];
  stateWiseDeviceBarChartData: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor: string[];
      borderColor: string[];
      borderWidth: number;
    }[];
  } = {
    labels: [],
    datasets: [
      {
        label: 'Count',
        data: [],
        backgroundColor: chartColorsList,
        borderColor: chartColorsList,
        borderWidth: 1,
      },
    ],
  };
  deviceActivityOverviewBarChartData: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor: string[];
      borderColor: string[];
      borderWidth: number;
    }[];
  } = {
    labels: [],
    datasets: [
      {
        label: 'Count',
        data: [],
        backgroundColor: chartColorsList,
        borderColor: chartColorsList,
        borderWidth: 1,
      },
    ],
  };
  barChartOption!: ChartConfiguration<'bar'>['options'];

  lineChartOptions!: ChartConfiguration<'line'>['options'];

  // Device Model wise graph - the two popup buttons used to each trigger
  // their own live, uncached, per-model query when opened; both breakdowns
  // now arrive for free as part of getDeviceDashboardSummary() (populated by
  // the same 15-minute cron as the rest of the dashboard), so opening either
  // popup just reprocesses this already-fetched data instead of a new call.
  deviceModelWiseFirmwareRawData: {
    _id: string;
    firmwareData: { firmwareVersion: string; count: number }[];
  }[] = [];
  deviceModelWiseActivityRawData: {
    modelName: string;
    inactiveCounts: number[];
  }[] = [];
  deviceModelWiseFirmwareNamesList = [];
  deviceModelWiseDeviceActivityOverviewGraph = [];
  validFirmware = [
    '5.2.8_REL24',
    '5.2.8',
    '5.2.6_REL07',
    '5.2.8_REL18',
    '5.2.3',
    '2.0.12',
    '5.2.6_REL04',
    '5.2.8_REL25',
    '5.2.6_REL05',
    '5.2.0',
    '5.2.6_rel05',
    '2.0.10',
    '5.2.6_REL03',
    '5.2.9_REL01',
    '5.2.9_REL05',
    '5.2.9_REL07',
    '5.2.9_REL09',
    '2.0.0',
    '2.0.14_TST14',
    '2.0.9',
    '2.0.14',
  ];

  // List
  listData: any[] = [];
  size = 10;
  totalCount = 0;
  page = 0;
  selectedComponentTitle = 'Total Production Devices';

  // One shared column set for every tab, including Active AIS140 Certified Devices
  // (backed by a different collection - certificationDetailTable, not the
  // device master - whose service projection aliases modelname/vinNo to
  // modelName/vin so it lines up with this same set). Columns that don't
  // apply to a given row (e.g. Firmware Version/Last Login At on a
  // certificate record, which has neither) just render "--", same as any
  // other missing field already does.
  unifiedHeader = [
    'UIN NO.',
    'IMEI NO.',
    'ICCID NO.',
    'Model Name',
    'VIN/Chassis No.',
    'Firmware Version',
    'State',
    'Last Login At',
  ];
  unifiedHeaderValue = [
    'uid',
    'imei',
    'iccid',
    'modelName',
    'vin',
    'firmwareVersion',
    'state',
    'loggedIntime',
  ];
  fourGModels = ['ACON4NA', 'ACON4TA', 'ACON4PA', 'AEPL051401', 'AEPL051400'];

  // Maps each tab (except Certified, handled on its own) to the backend
  // bucket key and the sessionStorage keys that remember its page/size/
  // search/filter across visits - kept identical to the per-section keys
  // used before this was unified, so persisted state doesn't reset.
  private bucketConfig: Record<
    string,
    {
      bucket: string;
      pageKey: string;
      sizeKey: string;
      searchKey: string;
      searchFormControl: string;
      filterKey?: string;
      filterFormControl?: string;
    }
  > = {
    'Total Production Devices': {
      bucket: 'total',
      pageKey: 'pageNoTotalProduct',
      sizeKey: 'sizeTotalProduct',
      searchKey: 'searchTotalProduct',
      searchFormControl: 'searchTotalProduct',
    },
    'Dispatched Devices': {
      bucket: 'dispatched',
      pageKey: 'pageNoDispatchedDevices',
      sizeKey: 'sizeDispatchedDevices',
      searchKey: 'searchDispatchedDevices',
      searchFormControl: 'searchDispatchedDevices',
    },
    'Installed Devices': {
      bucket: 'installed',
      pageKey: 'pageNoInstalledDevices',
      sizeKey: 'sizeInstalledDevices',
      searchKey: 'searchInstalledDevices',
      searchFormControl: 'searchInstalledDevices',
      filterKey: 'firmwareWiseSortByInstalledDeviceFilterValue',
      filterFormControl: 'firmwareWiseSortByInstalledDeviceFilterValue',
    },
    'Device Activity Overview': {
      bucket: 'activity',
      pageKey: 'pageNoDevicesActivityOverview',
      sizeKey: 'sizeDevicesActivityOverview',
      searchKey: 'searchDevicesActivityOverview',
      searchFormControl: 'searchDevicesActivityOverview',
      filterKey: 'devicesActivityOverviewSortByFilterValue',
      filterFormControl: 'devicesActivityOverviewSortBy',
    },
    'Firmware Wise Details': {
      bucket: 'firmware',
      pageKey: 'pageNoFirmwareWiseDetails',
      sizeKey: 'sizeFirmwareWiseDetails',
      searchKey: 'searchFirmwareWiseDetails',
      searchFormControl: 'searchFirmwareWiseDetails',
      filterKey: 'firmwareWiseSortByFilterValue',
      filterFormControl: 'firmwareWiseSortBy',
    },
    'State Wise Details': {
      bucket: 'state',
      pageKey: 'pageNoStateWiseDetails',
      sizeKey: 'sizeStateWiseDetails',
      searchKey: 'searchStateWiseDetails',
      searchFormControl: 'searchStateWiseDetails',
      filterKey: 'stateWiseSortByFilterValue',
      filterFormControl: 'stateWiseSortBy',
    },
    'Active AIS140 Certified Devices': {
      bucket: 'certified',
      pageKey: 'pageNoCertifiedDevices',
      sizeKey: 'sizeCertifiedDevices',
      searchKey: 'searchCertifiedDevices',
      searchFormControl: 'searchCertifiedDevices',
    },
  };

  // Dropdown
  firmwareNamesList: { key: string; value: string }[] = [];
  activityOverviewFilter = [
    { key: 'All', value: 'All' },
    { key: 'Today', value: '1' },
    { key: 'Five Days', value: '5' },
    { key: 'Ten Days', value: '10' },
    { key: 'Fifteen Days', value: '15' },
    { key: 'More Than Fifteen', value: '20' },
    { key: 'Not Active', value: '0' },
  ];
  stateNamesList: { key: string; value: string }[] = [];

  deviceDashboardForm: FormGroup = this.formBuilder.group({});

  userPermission = [];
  DeviceDashboardView = false;
  DeviceDetailsView = false;
  exportData = [];

  isDownloading = false;

  // storage listener for cross-tab sync
  private storageListener = (event: StorageEvent) => {
    if (event.key === 'downloadInProgress') {
      this.isDownloading = event.newValue === 'true';
    }
  };

  constructor(
    public router: Router,
    private formBuilder: FormBuilder,
    public apiMicroservice: ApiServiceService,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.applyChartTheme();
    this.checkPermission();

    // Only call the APIs if permission is granted
    if (this.DeviceDashboardView) {
      this.syncInitialDownloadState();
      this.setDeviceDashboardForm();
      this.getDeviceDashboardSummary();
      this.selectedComponentTitle =
        sessionStorage.getItem('selectedComponentTitle') || '';
      if (!this.selectedComponentTitle) {
        this.selectedComponentTitle = 'Total Production Devices';
        sessionStorage.setItem(
          'selectedComponentTitle',
          this.selectedComponentTitle
        );
      }
      this.fetchDeviceList(this.selectedComponentTitle);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.storageListener);
  }

  @HostListener('window:themeChanged')
  onThemeChanged() {
    this.applyChartTheme();
  }

  applyChartTheme() {
    this.barChartOption = buildChartTheme<'bar'>();
    this.barChartOption = {
      ...this.barChartOption,
      plugins: {
        ...this.barChartOption.plugins,
        legend: {
          display: false, // Enable legend to verify data binding
        },
      },
    };
  }

  checkPermission() {
    this.userPermission = JSON.parse(
      this.apiMicroservice.decryptData(
        sessionStorage.getItem('userPermission') || '[]'
      )
    );
    this.userPermission.forEach(
      (element: {
        pageName: string;
        view: boolean;
        create: boolean;
        delete: boolean;
        update: boolean;
      }) => {
        if (element.pageName === 'Device Dashboard' && element.view === true) {
          this.DeviceDashboardView = true;
        }
        if (element.pageName === 'Device Details' && element.view === true) {
          this.DeviceDetailsView = true;
        }
        if (element.pageName === 'Device Dashboard' && element.view === false) {
          this.navigateBack();
        }
      }
    );
  }

  syncInitialDownloadState() {
    // sync initial download state across tabs
    this.isDownloading = localStorage.getItem('downloadInProgress') === 'true';
    window.addEventListener('storage', this.storageListener);
  }
  setDeviceDashboardForm() {
    this.deviceDashboardForm = this.formBuilder.group({
      devicesActivityOverviewSortBy: new FormControl(''),
      firmwareWiseSortBy: new FormControl(''),
      stateWiseSortBy: new FormControl(''),
      firmwareWiseSortByInstalledDeviceFilterValue: new FormControl(''),
      searchTotalProduct: new FormControl(''),
      searchDispatchedDevices: new FormControl(''),
      searchCertifiedDevices: new FormControl(''),
      searchInstalledDevices: new FormControl(''),
      searchDevicesActivityOverview: new FormControl(''),
      searchFirmwareWiseDetails: new FormControl(''),
      searchNewFirmwareStatus: new FormControl(''),
      searchStateWiseDetails: new FormControl(''),
    });
  }

  // KPI + Graph - merged into a single request (was countProductionDev() +
  // barGraph() as two separate calls); both are still just reads of the same
  // 15-minute cron-cached counters as before.
  getDeviceDashboardSummary() {
    this.apiMicroservice.getDeviceDashboardSummary().subscribe({
      next: (res) => {
        if (res.status) {
          const kpi = res.data.kpi;
          this.productionDeviceCount = kpi.totalProducedDeviceCount;
          this.dispatchedCount = kpi.totalProducedDeviceCount;
          this.installedCount = kpi.installedDeviceCount;
          this.aisCount = kpi.certifiedDeviceCount;

          const graph = res.data.graph;
          this.deviceModelWiseFirmwareRawData =
            graph.deviceModelWiseFirmwareData || [];
          this.deviceModelWiseActivityRawData =
            graph.deviceModelWiseActivityData || [];
          this.stateNamesList = [{ key: 'All', value: 'All' }];
          this.stateChartLabel = [];
          this.stateWiseDeviceBarChartData.datasets[0].data = [];
          this.firmwareNamesList = [];
          this.firmwareChartLabel = [];
          this.firmwareWiseDeviceBarChartData.datasets[0].data = [];
          if (graph.firmwareWiseDeviceGraphData.length > 0) {
            graph.firmwareWiseDeviceGraphData.forEach(
              (element: { _id: string; AFcount: number }) => {
                if (this.projectName == 'TCU4G') {
                  const item = { key: element._id, value: element._id };
                  if (this.validFirmware.includes(element._id)) {
                    this.firmwareChartLabel.push(
                      `${element._id} ➔ ${(element.AFcount || 0).toLocaleString('en-IN')}`
                    );
                    this.firmwareWiseDeviceBarChartData.datasets[0].data.push(
                      element.AFcount
                    );
                  }
                  this.firmwareNamesList.push(item);
                } else {
                  const item = { key: element._id, value: element._id };
                  this.firmwareNamesList.push(item);
                  this.firmwareChartLabel.push(
                    `${element._id} ➔ ${element.AFcount}`
                  );
                  this.firmwareWiseDeviceBarChartData.datasets[0].data.push(
                    element.AFcount
                  );
                }
              }
            );
          }

          if (graph.stateWiseDeviceGraphData.length > 0) {
            graph.stateWiseDeviceGraphData.forEach(
              (element: {
                _id: { id: string; state: string };
                count: number;
              }) => {
                const stateName = element._id?.state || 'Unassign';
                const item = {
                  key: stateName,
                  value: element._id?.id || 'Unassign',
                };
                this.stateNamesList.push(item);
                if (element._id.state) {
                  this.stateChartLabel.push(
                    `${stateName} ➔ ${(element.count || 0).toLocaleString('en-IN')}`
                  );
                  this.stateWiseDeviceBarChartData.datasets[0].data.push(
                    element.count
                  );
                }
              }
            );
          }

          this.deviceActivityOverviewBarChartData.datasets[0].data = [];
          if (graph.deviceActivityOverviewGraphData) {
            this.deviceActivityOverviewBarChartData.datasets[0].data = [
              graph.deviceActivityOverviewGraphData.todaysCount,
              graph.deviceActivityOverviewGraphData.fiveDaysCount,
              graph.deviceActivityOverviewGraphData.tenDaysCount,
              graph.deviceActivityOverviewGraphData.fifteenDaysCount,
              graph.deviceActivityOverviewGraphData.moreThanFifteenDaysCount,
              graph.deviceActivityOverviewGraphData.notActiveCount,
            ];

            this.deviceActivityOverviewBarChartData.labels = [
              `${this.activeLabel[0]} ➔ ${(graph.deviceActivityOverviewGraphData.todaysCount || 0).toLocaleString('en-IN')}`,
              `${this.activeLabel[1]} ➔ ${(graph.deviceActivityOverviewGraphData.fiveDaysCount || 0).toLocaleString('en-IN')}`,
              `${this.activeLabel[2]} ➔ ${(graph.deviceActivityOverviewGraphData.tenDaysCount || 0).toLocaleString('en-IN')}`,
              `${this.activeLabel[3]} ➔ ${(graph.deviceActivityOverviewGraphData.fifteenDaysCount || 0).toLocaleString('en-IN')}`,
              `${this.activeLabel[4]} ➔ ${(graph.deviceActivityOverviewGraphData.moreThanFifteenDaysCount || 0).toLocaleString('en-IN')}`,
              `${this.activeLabel[5]} ➔ ${(graph.deviceActivityOverviewGraphData.notActiveCount || 0).toLocaleString('en-IN')}`,
            ];
          } else {
            this.deviceActivityOverviewBarChartData.datasets[0].data = [
              0, 0, 0, 0, 0, 0,
            ];
          }
        } else {
          this.productionDeviceCount = 0;
          this.dispatchedCount = 0;
          this.installedCount = 0;
          this.aisCount = 0;
          this.firmwareWiseDeviceBarChartData.datasets[0].data = [];
          this.deviceActivityOverviewBarChartData.datasets[0].data = [
            0, 0, 0, 0, 0, 0,
          ];
        }
        this.firmwareWiseDeviceBarChartData = this.updateChartData(
          this.firmwareChartLabel,
          this.firmwareWiseDeviceBarChartData.datasets[0].data,
          'Count'
        );

        this.stateWiseDeviceBarChartData = this.updateChartData(
          this.stateChartLabel,
          this.stateWiseDeviceBarChartData.datasets[0].data,
          'Count'
        );

        this.deviceActivityOverviewBarChartData = this.updateChartData(
          this.deviceActivityOverviewBarChartData.labels,
          this.deviceActivityOverviewBarChartData.datasets[0].data,
          'Count'
        );
      },
      error: (ERROR: HttpErrorResponse) => {
        this.productionDeviceCount = 0;
        this.dispatchedCount = 0;
        this.installedCount = 0;
        this.aisCount = 0;
        this.firmwareWiseDeviceBarChartData.datasets[0].data = [];
        this.deviceActivityOverviewBarChartData.datasets[0].data = [
          0, 0, 0, 0, 0, 0,
        ];
        this.apiMicroservice.openSnackbar(
          ERROR.error.message || 'Dashboard summary not fetched'
        );
        this.spinner.hide();
      },
    });
  }

  // Lists
  fetchDeviceList(pageTitle: string) {
    this.spinner.show();
    this.listData = [];

    setTimeout(() => {
      if (this.MyProp) {
        this.MyProp.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);

    this.selectedComponentTitle = pageTitle;
    sessionStorage.setItem(
      'selectedComponentTitle',
      this.selectedComponentTitle
    );

    const config = this.bucketConfig[this.selectedComponentTitle];
    if (!config) {
      this.spinner.hide();
      return;
    }

    // Search
    const savedSearch = sessionStorage.getItem(config.searchKey) || '';
    this.deviceDashboardForm
      ?.get(config.searchFormControl)
      ?.setValue(savedSearch);
    const search = this.deviceDashboardForm?.get(
      config.searchFormControl
    )?.value;

    // Page number
    let page = 1;
    if (parseInt(sessionStorage.getItem(config.pageKey) || '0')) {
      page = parseInt(sessionStorage.getItem(config.pageKey) || '0');
    } else {
      sessionStorage.setItem(config.pageKey, page.toString());
    }
    this.page = page;

    // Page size
    let size = 10;
    if (parseInt(sessionStorage.getItem(config.sizeKey) || '0')) {
      size = parseInt(sessionStorage.getItem(config.sizeKey) || '0');
    } else {
      sessionStorage.setItem(config.sizeKey, size.toString());
    }
    this.size = size;

    // Bucket-specific extra filter (activity day-bucket, firmware
    // version(s), installed-firmware, or state), if this tab has one
    let filterValue = '';
    if (config.filterKey) {
      filterValue = sessionStorage.getItem(config.filterKey) || '';
      if (config.filterFormControl) {
        this.deviceDashboardForm
          ?.get(config.filterFormControl)
          ?.setValue(filterValue);
      }
      sessionStorage.setItem(config.filterKey, filterValue);
    }

    this.apiMicroservice
      .getDeviceDashboardList(
        config.bucket,
        this.page,
        this.size,
        search,
        filterValue
      )
      .subscribe({
        next: (res) => {
          if (res.status) {
            this.listData = (res.data.data || []).map((item: any) => ({
              ...item,
              // Device-collection rows arrive with a populated
              // { state: '...' } sub-document; Certified Devices rows
              // (a different collection) already have state as a plain
              // string - handle both shapes.
              state:
                (typeof item.state === 'string'
                  ? item.state
                  : item.state?.state) || 'Unassign',
              loggedIntime: item.loggedIntime
                ? new Date(item.loggedIntime).toLocaleString('en-GB', {
                    timeZone: 'Asia/Kolkata',
                    hour12: true,
                  })
                : null,
            }));
            this.totalCount = res.data.totalItems;
            this.page = res.data.pageNumber;
            this.apiMicroservice.openSnackbar('Fetched Successfully');
          } else {
            this.apiMicroservice.openSnackbar(res.message);
          }
          this.spinner.hide();
        },
        error: (ERROR: HttpErrorResponse) => {
          this.spinner.hide();
          this.apiMicroservice.openSnackbar(ERROR.error?.message);
        },
      });
  }

  // Device Model Wise Graphs - both popups below just reprocess the
  // raw breakdown already fetched by getDeviceDashboardSummary() (no HTTP
  // call here anymore; see the field comments above for why).
  deviceModelWiseFirmwareDetailsGraph() {
    document
      ?.getElementById('deviceModelWiseFirmwareDetailsGraph')
      ?.parentElement?.classList.add('active');
    this.deviceModelWiseFirmwareNamesList = [];
    this.deviceModelWiseFirmwareRawData.forEach((element) => {
      const labels: string[] = [];
      const data: number[] = [];
      element.firmwareData.forEach((element1) => {
        if (this.projectName == 'TCU4G') {
          if (this.validFirmware.includes(element1.firmwareVersion)) {
            labels.push(
              `${element1.firmwareVersion} ➔ ${(element1.count || 0).toLocaleString('en-IN')}`
            );
            data.push(element1.count);
          }
        } else {
          labels.push(
            `${element1.firmwareVersion} ➔ ${(element1.count || 0).toLocaleString('en-IN')}`
          );
          data.push(element1.count);
        }
      });

      const updatedGraph = this.updateChartData(labels, data, element._id);
      this.deviceModelWiseFirmwareNamesList.push(updatedGraph);
    });
  }

  downloadFirmwareWiseGraphDetailsReport(modelName: string) {
    if (confirm('Are you sure?')) {
      // mark download in-progress and notify other tabs
      this.isDownloading = true;
      localStorage.setItem('downloadInProgress', 'true');

      this.apiMicroservice.openSnackbar(
        'Your file is being generated. Refreshing the page or logging out may interrupt the download.'
      );
      this.apiMicroservice
        .getDeviceDashboardDownload('firmware', '', '', modelName)
        .subscribe({
          next: (res) => {
            try {
              if (res?.status && res.fileDownloadUrl) {
                // window.open(res.fileDownloadUrl, '_blank');
                const link = document.createElement('a');
                link.href = res.fileDownloadUrl;
                link.download = ''; // let browser infer filename
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.apiMicroservice.openSnackbar('File ready for download');
              } else {
                this.apiMicroservice.openSnackbar(
                  res?.message || 'File URL not available'
                );
              }
            } finally {
              // clear in-progress state
              this.isDownloading = false;
              localStorage.setItem('downloadInProgress', 'false');
            }
          },
          error: (ERROR: HttpErrorResponse) => {
            const example =
              ERROR.error.message ||
              'Download failed. Please contact server team.';
            this.apiMicroservice.openSnackbar(example);
            // clear in-progress state
            this.isDownloading = false;
            localStorage.setItem('downloadInProgress', 'false');
          },
        });
    }
  }

  deviceModelWiseDeviceActivityOverview() {
    document
      ?.getElementById('DeviceModelWiseDeviceActivityOverviewGraph')
      ?.parentElement?.classList.add('active');
    this.deviceModelWiseDeviceActivityOverviewGraph = [];
    this.deviceModelWiseActivityRawData.forEach((element) => {
      const labels: string[] = [];
      const data: number[] = [];

      element.inactiveCounts.forEach((inactiveCount: number, idx: number) => {
        if (this.activeLabel[idx]) {
          labels.push(
            `${this.activeLabel[idx]} ➔ ${(inactiveCount || 0).toLocaleString('en-IN')}`
          );
          data.push(inactiveCount);
        }
      });
      const updatedGraph = this.updateChartData(
        labels,
        data,
        element.modelName
      );
      this.deviceModelWiseDeviceActivityOverviewGraph.push(updatedGraph);
    });
  }

  downloadAllDeviceActivityReport(modelName: string) {
    if (confirm('Are you sure?')) {
      // mark download in-progress and notify other tabs
      this.isDownloading = true;
      localStorage.setItem('downloadInProgress', 'true');

      this.apiMicroservice.openSnackbar(
        'Your file is being generated. Refreshing the page or logging out may interrupt the download.'
      );
      this.apiMicroservice
        .getDeviceDashboardDownload('activity', '', '', modelName)
        .subscribe({
          next: (res) => {
            try {
              if (res?.status && res.fileDownloadUrl) {
                // window.open(res.fileDownloadUrl, '_blank');
                const link = document.createElement('a');
                link.href = res.fileDownloadUrl;
                link.download = ''; // let browser infer filename
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.apiMicroservice.openSnackbar('File ready for download');
              } else {
                this.apiMicroservice.openSnackbar(
                  res?.message || 'File URL not available'
                );
              }
            } finally {
              // clear in-progress state
              this.isDownloading = false;
              localStorage.setItem('downloadInProgress', 'false');
            }
          },
          error: (ERROR: HttpErrorResponse) => {
            const example =
              ERROR.error.message ||
              'Download failed. Please contact server team.';
            this.apiMicroservice.openSnackbar(example);
            // clear in-progress state
            this.isDownloading = false;
            localStorage.setItem('downloadInProgress', 'false');
          },
        });
    }
  }

  updateChartData(
    newLabels: string[],
    newData: (string | number)[],
    modelName: string
  ) {
    return cloneDeep({
      labels: newLabels,
      datasets: [
        {
          label: modelName || 'Unknown',
          data: newData.map((value) => Number(value)),
          backgroundColor: chartColorsList,
          borderColor: chartColorsList,
          borderWidth: 1,
        },
      ],
    });
  }

  // Export Functions
  downloadAllDeviceReport() {
    if (confirm('Are you sure?')) {
      this.apiMicroservice.openSnackbar(
        'File will be ready for download shortly. You will be notified once it is ready.'
      );
      this.spinner.show();
      this.apiMicroservice.downloadAllDeviceReport().subscribe({
        next: (res) => {
          if (res?.status && res.fileDownloadUrl) {
            // window.open(res.fileDownloadUrl, '_blank');
            const link = document.createElement('a');
            link.href = res.fileDownloadUrl;
            link.download = ''; // let browser infer filename
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            this.apiMicroservice.openSnackbar('File ready for download');
          } else {
            this.apiMicroservice.openSnackbar(
              res?.message || 'File URL not available'
            );
          }
          this.spinner.hide();
        },
        error: (ERROR: HttpErrorResponse) => {
          const example =
            ERROR.error.message ||
            'Download failed. Please contact server team.';
          this.apiMicroservice.openSnackbar(example);
          this.spinner.hide();
        },
      });
    }
  }

  // Single export function backing the download button on every unified
  // tab (Dispatched, Installed, Device Activity Overview, Firmware Wise,
  // State Wise) - replaces exportToExcelDispatchedDeviceData,
  // exportToExcelInstalledDevices, exportToExcelDeviceNotActiveFromData,
  // exportToExcelFirmwareWiseDetails and exportToExcelStateWiseDetails.
  // Total Production and Certified Devices keep their own separate export
  // functions (a static daily dump and a different collection, respectively).
  exportToExcelDashboardList() {
    const config = this.bucketConfig[this.selectedComponentTitle];
    if (!config) {
      return;
    }
    if (confirm('Are you sure?')) {
      const search =
        this.deviceDashboardForm?.get(config.searchFormControl)?.value || '';
      const filterValue = config.filterFormControl
        ? this.deviceDashboardForm?.get(config.filterFormControl)?.value || ''
        : '';

      // mark download in-progress and notify other tabs
      this.isDownloading = true;
      localStorage.setItem('downloadInProgress', 'true');

      this.apiMicroservice.openSnackbar(
        'Your file is being generated. Refreshing the page or logging out may interrupt the download.'
      );

      this.apiMicroservice
        .getDeviceDashboardDownload(config.bucket, search, filterValue)
        .subscribe({
          next: (res) => {
            try {
              if (res?.status && res.fileDownloadUrl) {
                const link = document.createElement('a');
                link.href = res.fileDownloadUrl;
                link.download = ''; // let browser infer filename
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.apiMicroservice.openSnackbar('File ready for download');
              } else {
                this.apiMicroservice.openSnackbar(
                  res?.message || 'File URL not available'
                );
              }
            } finally {
              // clear in-progress state
              this.isDownloading = false;
              localStorage.setItem('downloadInProgress', 'false');
            }
          },
          error: (ERROR: HttpErrorResponse) => {
            const example =
              ERROR.error?.message ||
              'Download failed. Please contact server team.';
            this.apiMicroservice.openSnackbar(example);
            // clear in-progress state
            this.isDownloading = false;
            localStorage.setItem('downloadInProgress', 'false');
          },
        });
    }
  }

  // Page Change

  onPageChange(event: number) {
    this.page = event;
    if (this.selectedComponentTitle === 'Total Production Devices') {
      sessionStorage.setItem('pageNoTotalProduct', this.page.toString());
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'Dispatched Devices') {
      sessionStorage.setItem('pageNoDispatchedDevices', this.page.toString());
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (
      this.selectedComponentTitle === 'Active AIS140 Certified Devices'
    ) {
      sessionStorage.setItem('pageNoCertifiedDevices', this.page.toString());
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'Installed Devices') {
      sessionStorage.setItem('pageNoInstalledDevices', this.page.toString());
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'Device Activity Overview') {
      sessionStorage.setItem(
        'pageNoDevicesActivityOverview',
        this.page.toString()
      );
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'Firmware Wise Details') {
      sessionStorage.setItem('pageNoFirmwareWiseDetails', this.page.toString());
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'State Wise Details') {
      sessionStorage.setItem('pageNoStateWiseDetails', this.page.toString());
      this.fetchDeviceList(this.selectedComponentTitle);
    } else {
      this.spinner.hide();
    }
  }

  onSizeChange(event: number) {
    this.page = 1;
    this.size = event;
    if (this.selectedComponentTitle === 'Total Production Devices') {
      sessionStorage.setItem('pageNoTotalProduct', this.page.toString());
      sessionStorage.setItem('sizeTotalProduct', this.size.toString());
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'Dispatched Devices') {
      sessionStorage.setItem('pageNoDispatchedDevices', this.page.toString());
      sessionStorage.setItem('sizeDispatchedDevices', this.size.toString());
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (
      this.selectedComponentTitle === 'Active AIS140 Certified Devices'
    ) {
      sessionStorage.setItem('pageNoCertifiedDevices', this.page.toString());
      sessionStorage.setItem('sizeCertifiedDevices', this.size.toString());
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'Installed Devices') {
      sessionStorage.setItem('pageNoInstalledDevices', this.page.toString());
      sessionStorage.setItem('sizeInstalledDevices', this.size.toString());
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'Device Activity Overview') {
      sessionStorage.setItem(
        'pageNoDevicesActivityOverview',
        this.page.toString()
      );
      sessionStorage.setItem(
        'sizeDevicesActivityOverview',
        this.size.toString()
      );
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'Firmware Wise Details') {
      sessionStorage.setItem('pageNoFirmwareWiseDetails', this.page.toString());
      sessionStorage.setItem('sizeFirmwareWiseDetails', this.size.toString());
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'State Wise Details') {
      sessionStorage.setItem('pageNoStateWiseDetails', this.page.toString());
      sessionStorage.setItem('sizeStateWiseDetails', this.size.toString());
      this.fetchDeviceList(this.selectedComponentTitle);
    } else {
      this.spinner.hide();
    }
  }

  onBulkSearch(values: string) {
    const combinedSearch = values
      .split(',')
      .map((value) => value.trim())
      .filter((value) => !!value)
      .join(',');

    if (!combinedSearch) {
      this.apiMicroservice.openSnackbar(
        'No valid search values found in the uploaded sheet'
      );
      return;
    }

    this.onSearch(combinedSearch);
  }

  // Search function
  onSearch(event: string) {
    this.page = 1;
    const search = event;
    if (this.selectedComponentTitle === 'Total Production Devices') {
      sessionStorage.setItem('pageNoTotalProduct', this.page.toString());
      sessionStorage.setItem('searchTotalProduct', search);
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'Dispatched Devices') {
      sessionStorage.setItem('pageNoDispatchedDevices', this.page.toString());
      sessionStorage.setItem('searchDispatchedDevices', search);
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (
      this.selectedComponentTitle === 'Active AIS140 Certified Devices'
    ) {
      sessionStorage.setItem('pageNoCertifiedDevices', this.page.toString());
      sessionStorage.setItem('searchCertifiedDevices', search);
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'Installed Devices') {
      sessionStorage.setItem('pageNoInstalledDevices', this.page.toString());
      sessionStorage.setItem('searchInstalledDevices', search);
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'Device Activity Overview') {
      sessionStorage.setItem(
        'pageNoDevicesActivityOverview',
        this.page.toString()
      );
      sessionStorage.setItem('searchDevicesActivityOverview', search);
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'Firmware Wise Details') {
      sessionStorage.setItem('pageNoFirmwareWiseDetails', this.page.toString());
      sessionStorage.setItem('searchFirmwareWiseDetails', search);
      this.fetchDeviceList(this.selectedComponentTitle);
    } else if (this.selectedComponentTitle === 'State Wise Details') {
      sessionStorage.setItem('pageNoStateWiseDetails', this.page.toString());
      sessionStorage.setItem('searchStateWiseDetails', search);
      this.fetchDeviceList(this.selectedComponentTitle);
    } else {
      this.spinner.hide();
    }
  }

  // Filter change function
  onFirmwareWiseSelectionForInstalledChange(event: string) {
    this.page = 1;
    sessionStorage.setItem('pageNoInstalledDevices', this.page.toString());
    sessionStorage.setItem(
      'firmwareWiseSortByInstalledDeviceFilterValue',
      event
    );
    this.fetchDeviceList(this.selectedComponentTitle);
  }

  onDevicesNotActiveSelectionChange(event: string) {
    this.page = 1;
    sessionStorage.setItem(
      'pageNoDevicesActivityOverview',
      this.page.toString()
    );
    sessionStorage.setItem('devicesActivityOverviewSortByFilterValue', event);
    this.fetchDeviceList(this.selectedComponentTitle);
  }

  onFirmwareWiseSelectionChange(event: string) {
    this.page = 1;
    sessionStorage.setItem('pageNoFirmwareWiseDetails', this.page.toString());

    sessionStorage.setItem('firmwareWiseSortByFilterValue', event);
    this.fetchDeviceList(this.selectedComponentTitle);
  }

  onStateWiseSelectionChange(event: string) {
    this.page = 1;
    sessionStorage.setItem('pageNoStateWiseDetails', this.page.toString());
    sessionStorage.setItem('stateWiseSortByFilterValue', event);
    this.fetchDeviceList(this.selectedComponentTitle);
  }

  // Redirect

  view(id: string, imei: string) {
    sessionStorage.setItem('uid', id);
    sessionStorage.setItem('imei', imei);
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/device-details'])
    );
    const fullUrl = window.location.origin + url;

    // Open the URL in a new tab
    window.open(fullUrl, '_blank');
    // this.router.navigate(["device-details"])
  }

  closeModal() {
    document
      ?.getElementById('deviceModelWiseFirmwareDetailsGraph')
      ?.parentElement?.classList.remove('active');
    document
      ?.getElementById('DeviceModelWiseDeviceActivityOverviewGraph')
      ?.parentElement?.classList.remove('active');
  }

  navigateBack() {
    this.apiMicroservice.back();
  }
}
